import { BRAZIL_UF_SELECT_OPTIONS } from '@/features/proposals/components/capital-global/constants/brazil-uf-select-options.constant';
import type { Broker } from '@/services';
import {
  useDataTablePageSync,
  useIsInsurerAdminOrCx,
  useIsInsurerUser,
  useRequirePermissions,
} from '@/shared/hooks';
import { SearchFilterField, SelectFilterField } from '@/shared/ui';
import { Page, PageHeader } from '@/shared/ui/page';
import { f, useFilters } from '@/shared/utils';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import { formatZipcode } from '@/shared/utils/format-zipcode.util';
import {
  Avatar,
  Button,
  DataTable,
  DataTableColumn,
  Menu,
  Pagination,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { Eye, Home, Layers, Mail, UsersRound } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAdvisoryFilterOptions, useBrokers } from '../hooks';
import { isActiveBroker } from '../utils/broker-status.util';
import { BulkProductsModal } from './bulk-products-modal.component';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 30, 40, 50];
const DEFAULT_SORT_FIELD = 'PartyName';
const DEFAULT_SORT_TYPE = 'asc';

const BROKER_SORT_FIELD_BY_COLUMN_ID: Record<string, string> = {
  name: 'PartyName',
  cnpj: 'OrganizationIdNumber',
};

const BROKER_COLUMN_ID_BY_SORT_FIELD: Record<string, string> = {
  PartyName: 'name',
  OrganizationIdNumber: 'cnpj',
};

const EMPTY = '—';

/** O cadastro do InsureMO tem e-mail em caixa alta; a tela sempre exibe minúsculo. */
function formatEmail(broker: Broker) {
  return broker.email ? broker.email.toLowerCase() : 'N/A';
}

/** "São José dos Campos/SP", ou só o que existir. */
function formatLocation(broker: Broker) {
  const city = broker.city?.trim();
  const state = broker.state?.trim();

  if (city && state) return `${city}/${state}`;
  return city || state || '';
}

/** Logradouro, número e complemento numa linha só. */
function formatStreet(broker: Broker) {
  const street = [broker.street, broker.streetNumber]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(', ');

  return [street, broker.complement?.trim()].filter(Boolean).join(' - ');
}

function BrokerStatusTag({ broker }: { broker: Broker }) {
  const isActive = isActiveBroker(broker);

  return (
    <Tag.Root
      variant='outline'
      colorScheme={isActive ? 'success' : 'gray'}
      size='sm'
    >
      <Tag.Label>{isActive ? 'Ativo' : 'Inativo'}</Tag.Label>
    </Tag.Root>
  );
}

export function BrokersPage() {
  const navigate = useNavigate();
  const { hasPermission: canViewBrokerDetails } = useRequirePermissions({
    permissions: ['parties.brokers.users.read'],
  });
  // O PartyFilterInterceptor força o advisoryId do próprio usuário para perfis
  // de assessoria e corretora, então o filtro por assessoria só faz sentido (e
  // só tem efeito) para a seguradora. O mesmo vale para a coluna: fora da
  // seguradora, todas as linhas seriam da mesma assessoria.
  const { isInsurerUser } = useIsInsurerUser();
  const canFilterByAdvisory = isInsurerUser === true;

  // Gate de conveniência (Seg Adm e Seg CX) — o guard de verdade é o
  // @RequireGroups do endpoint.
  const { isInsurerAdminOrCx } = useIsInsurerAdminOrCx();
  const { hasPermission: canWriteProducts } = useRequirePermissions({
    permissions: ['parties.brokers.products.write'],
  });
  const canBulkManageProducts =
    isInsurerAdminOrCx === true && Boolean(canWriteProducts);

  const [isBulkOpen, setIsBulkOpen] = useState(false);

  const { filters, debouncedFilters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    advisoryId: f.string().withDefault(''),
    city: f.string().withDefault('').withDebounce(500),
    state: f.string().withDefault(''),
    page: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(10),
    sortField: f.string().withDefault(DEFAULT_SORT_FIELD),
    sortType: f.string().withDefault(DEFAULT_SORT_TYPE),
  });
  const dataTableRef = useDataTablePageSync<Broker>(filters.page);

  const { items: advisoryOptions, nameByEntityId: advisoryNameByEntityId } =
    useAdvisoryFilterOptions(canFilterByAdvisory);

  const {
    data: results,
    isLoading,
    error,
  } = useBrokers({
    ...debouncedFilters,
    advisoryId: canFilterByAdvisory ? debouncedFilters.advisoryId : '',
    sortField: filters.sortField,
    sortType: filters.sortType === 'desc' ? 'desc' : 'asc',
  });

  const brokers = results?.data || [];
  const pagination = results?.meta as unknown as {
    total: number;
    totalPages: number;
  };

  /** Toda troca de filtro volta para a primeira página. */
  const applyFilter = (patch: Partial<typeof filters>) =>
    setFilters({ ...filters, ...patch, page: 1 });

  const hasActiveFilters = Boolean(
    filters.search || filters.advisoryId || filters.city || filters.state
  );

  const clearFilters = () =>
    setFilters({
      ...filters,
      search: '',
      advisoryId: '',
      city: '',
      state: '',
      page: 1,
    });

  const resolveAdvisoryName = (broker: Broker) =>
    (broker.advisoryId && advisoryNameByEntityId.get(broker.advisoryId)) || '';

  const columns = useMemo<DataTableColumn<Broker>[]>(() => {
    const advisoryColumn: DataTableColumn<Broker>[] = canFilterByAdvisory
      ? [
          {
            id: 'advisory',
            header: 'Assessoria',
            size: 300,
            sortable: false,
            cell: ({ row }: { row: { original: Broker } }) => {
              const advisoryName = resolveAdvisoryName(row.original);
              return (
                <span
                  className='text-alba-gray-700 text-sm'
                  title={row.original.advisoryId || undefined}
                >
                  {advisoryName || EMPTY}
                </span>
              );
            },
          },
        ]
      : [];

    return [
      {
        accessorKey: 'name',
        header: 'Nome',
        size: 325,
        cell: ({ row }) => {
          const broker = row.original;
          return (
            <div className='flex items-center gap-3'>
              <Avatar.Root
                size='md'
                shape='circle'
                className='bg-alba-blue-500'
              >
                <UsersRound className='h-4 w-4 text-white' />
              </Avatar.Root>
              <div className='flex flex-col gap-1'>
                <span className='font-gotham font-bold text-alba-black-500'>
                  {broker.name}
                </span>
                <div className='flex items-center gap-1 text-alba-gray-600'>
                  <Mail className='h-4 w-4' />
                  <span className='text-xs break-all'>
                    {formatEmail(broker)}
                  </span>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'cnpj',
        header: 'CNPJ',
        // CNPJ formatado tem largura fixa (18 caracteres), então não precisa
        // de folga nem pode quebrar linha.
        size: 165,
        cell: ({ row }) => {
          const broker = row.original;
          return (
            <span className='whitespace-nowrap text-alba-gray-700 text-sm'>
              {formatRegisterNumber(broker.licenseNumber)}
            </span>
          );
        },
      },
      {
        id: 'susep',
        header: 'SUSEP',
        size: 105,
        sortable: false,
        cell: ({ row }: { row: { original: Broker } }) => (
          <span className='whitespace-nowrap text-alba-gray-700 text-sm'>
            {row.original.susepCode || EMPTY}
          </span>
        ),
      },
      ...advisoryColumn,
      {
        id: 'location',
        header: 'Localidade',
        size: 190,
        sortable: false,
        cell: ({ row }: { row: { original: Broker } }) => {
          const broker = row.original;
          const location = formatLocation(broker);

          if (!location) {
            return <span className='text-alba-gray-700 text-sm'>{EMPTY}</span>;
          }

          return (
            <div className='flex flex-col gap-1'>
              <span className='text-alba-gray-700 text-sm'>{location}</span>
              {broker.neighborhood && (
                <span className='text-xs text-alba-gray-600'>
                  {broker.neighborhood}
                </span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        // O piso real é o cabeçalho + o padding da célula; abaixo disso o
        // navegador ignora a largura pedida.
        size: 70,
        sortable: false,
        cell: ({ row }) => <BrokerStatusTag broker={row.original} />,
      },
      ...(canViewBrokerDetails
        ? [
            {
              id: 'actions',
              header: 'Ações',
              size: 50,
              sortable: false,
              cell: ({ row }: { row: { original: Broker } }) => {
                const broker = row.original;
                return (
                  <div className='flex justify-end'>
                    <Menu.Root>
                      <Menu.Trigger />
                      <Menu.Content>
                        <Menu.Item
                          value='detail'
                          icon={<Eye size={16} />}
                          onSelect={() =>
                            navigate({
                              to: `/brokers/${broker.code}`,
                            })
                          }
                        >
                          Ver detalhes
                        </Menu.Item>
                      </Menu.Content>
                    </Menu.Root>
                  </div>
                );
              },
            },
          ]
        : []),
    ];
  }, [
    navigate,
    canViewBrokerDetails,
    canFilterByAdvisory,
    advisoryNameByEntityId,
  ]);

  const sorting = useMemo(() => {
    const columnId = BROKER_COLUMN_ID_BY_SORT_FIELD[filters.sortField];

    if (!columnId) return [];

    return [
      {
        id: columnId,
        desc: filters.sortType === 'desc',
      },
    ];
  }, [filters.sortField, filters.sortType]);

  return (
    <Page.Root>
      <Page.Breadcrumb
        items={[
          {
            label: 'Página Inicial',
            href: '/dashboard',
            iconLeft: <Home className='h-4 w-4' />,
          },
          { label: 'Gestão' },
          { label: 'Corretores', active: true },
        ]}
      />

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>Corretores</PageHeader.Title>
            <PageHeader.Subtitle>
              Gerencie as corretoras parceiras
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
        </PageHeader.Main>

        {canBulkManageProducts && (
          <PageHeader.Actions>
            <Button
              variant='outline'
              onClick={() => setIsBulkOpen(true)}
              title='Ativar ou desativar produtos em varias corretoras de uma vez'
              className='w-full md:w-auto'
            >
              {/* Icone no fluxo: o Button ja aplica `gap-2` e centraliza o
                  conjunto icone+rotulo. Tirar o icone do fluxo centralizava o
                  texto no botao, mas encostava os dois quando o rotulo crescia. */}
              <Layers className='h-5 w-5 shrink-0' strokeWidth={1.8} />
              Gerenciar produtos em lote
            </Button>
          </PageHeader.Actions>
        )}
      </PageHeader.Root>

      <PageHeader.Filters>
        <div className='flex w-full flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-4'>
          <SearchFilterField
            {...registerFilter('search')}
            onChange={value => applyFilter({ search: String(value ?? '') })}
            placeholder='Buscar por nome, CNPJ ou email...'
            // Sem teto de largura: e este campo que absorve a sobra e faz a
            // linha de filtros terminar na mesma borda da tabela.
            className='w-full md:min-w-[260px] md:flex-1'
          />

          {canFilterByAdvisory && (
            <SelectFilterField
              {...registerFilter('advisoryId')}
              items={advisoryOptions}
              placeholder='Assessoria'
              className='w-full md:min-w-[220px] md:max-w-[260px]'
              onValueChange={value => applyFilter({ advisoryId: value })}
            />
          )}

          <SelectFilterField
            {...registerFilter('state')}
            items={BRAZIL_UF_SELECT_OPTIONS}
            placeholder='Estado'
            className='w-full md:min-w-[180px] md:max-w-[200px]'
            onValueChange={value => applyFilter({ state: value })}
          />

          <SearchFilterField
            {...registerFilter('city')}
            onChange={value => applyFilter({ city: String(value ?? '') })}
            placeholder='Cidade'
            className='w-full md:min-w-[200px] md:max-w-[240px]'
          />

          {hasActiveFilters && (
            <button
              type='button'
              onClick={clearFilters}
              className='self-start text-sm font-bold text-alba-blue-500 underline-offset-4 hover:underline md:self-auto'
            >
              Limpar filtros
            </button>
          )}
        </div>
      </PageHeader.Filters>

      <Page.Content className='min-w-0'>
        {error ? (
          <div className='py-8 text-center text-alba-error-red-500'>
            Erro ao carregar corretores. Tente novamente.
          </div>
        ) : (
          <>
            <div className='space-y-4 md:hidden'>
              {isLoading
                ? Array.from({ length: Math.min(filters.pageSize, 3) }).map(
                    (_, index) => (
                      <div
                        key={index}
                        className='h-52 animate-pulse rounded-3xl bg-alba-gray-200'
                      />
                    )
                  )
                : brokers.map(broker => {
                    const location = formatLocation(broker);
                    const street = formatStreet(broker);
                    const advisoryName = canFilterByAdvisory
                      ? resolveAdvisoryName(broker)
                      : '';

                    return (
                      <article
                        key={broker.id}
                        className='rounded-3xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
                      >
                        <div className='flex items-start justify-between gap-3'>
                          <div className='flex min-w-0 items-center gap-3'>
                            <Avatar.Root
                              size='md'
                              shape='circle'
                              className='bg-alba-blue-500'
                            >
                              <UsersRound className='h-4 w-4 text-white' />
                            </Avatar.Root>
                            <div className='min-w-0'>
                              <p
                                className='font-gotham font-bold text-alba-black-500 [overflow-wrap:anywhere]'
                                title={broker.name}
                              >
                                {broker.name}
                              </p>
                              <p
                                className='mt-1 text-xs text-alba-gray-600 [overflow-wrap:anywhere]'
                                title={formatEmail(broker)}
                              >
                                {formatEmail(broker)}
                              </p>
                            </div>
                          </div>
                          {canViewBrokerDetails && (
                            <Menu.Root>
                              <Menu.Trigger />
                              <Menu.Content>
                                <Menu.Item
                                  value='detail'
                                  icon={<Eye size={16} />}
                                  onSelect={() =>
                                    navigate({
                                      to: `/brokers/${broker.code}`,
                                    })
                                  }
                                >
                                  Ver detalhes
                                </Menu.Item>
                              </Menu.Content>
                            </Menu.Root>
                          )}
                        </div>

                        <div className='mt-4 grid grid-cols-2 gap-3 border-t border-alba-gray-200 pt-4'>
                          <BrokerCardField
                            label='CNPJ'
                            value={formatRegisterNumber(broker.licenseNumber)}
                          />
                          <BrokerCardField
                            label='SUSEP'
                            value={broker.susepCode}
                          />
                          {advisoryName && (
                            <BrokerCardField
                              label='Assessoria'
                              value={advisoryName}
                              className='col-span-2'
                            />
                          )}
                          <div className='col-span-2'>
                            <p className='mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                              Status
                            </p>
                            <BrokerStatusTag broker={broker} />
                          </div>
                        </div>

                        {(location || street) && (
                          /* Mesmo formato rotulado dos demais campos: o icone
                             solto a esquerda destoava da grade e ainda empurrava
                             o texto numa coluna estreita. */
                          <div className='mt-4 border-t border-alba-gray-200 pt-3'>
                            <p className='mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                              Endereço
                            </p>
                            {street && (
                              <p className='text-sm text-alba-gray-700 [overflow-wrap:anywhere]'>
                                {street}
                              </p>
                            )}
                            <p className='text-xs text-alba-gray-600 [overflow-wrap:anywhere]'>
                              {[
                                broker.neighborhood,
                                location,
                                broker.zipCode
                                  ? formatZipcode(broker.zipCode)
                                  : '',
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                          </div>
                        )}
                      </article>
                    );
                  })}

              {!isLoading && !brokers.length && (
                <div className='rounded-3xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
                  Nenhum corretor encontrado.
                </div>
              )}

              {(pagination?.totalPages || 0) > 0 && (
                <div className='flex justify-center'>
                  <Pagination
                    count={(pagination?.totalPages || 0) * filters.pageSize}
                    page={filters.page}
                    pageSize={filters.pageSize}
                    totalItems={pagination?.total || 0}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    onPageChange={page =>
                      setFilters({
                        ...filters,
                        page: page.page,
                        pageSize: page.pageSize,
                      })
                    }
                    onPageSizeSelect={pageSize =>
                      setFilters({ ...filters, page: 1, pageSize })
                    }
                  />
                </div>
              )}
            </div>

            <div className='hidden min-w-0 overflow-hidden rounded-3xl md:block'>
              <DataTable
                ref={dataTableRef}
                // O design system aplica p-4 em th/td, o que soma 32px entre
                // uma coluna e outra. Aqui o padding lateral cai para 8px
                // (16px de respiro entre colunas), mantendo os 16px originais
                // nas bordas da tabela. O seletor de descendente tem
                // especificidade maior que o p-4 do pacote, então prevalece
                // sem !important.
                className={[
                  'min-w-0',
                  '[&_th]:px-2 [&_td]:px-2',
                  '[&_th:first-child]:pl-4 [&_td:first-child]:pl-4',
                  '[&_th:last-child]:pr-4 [&_td:last-child]:pr-4',
                ].join(' ')}
                loading={isLoading}
                columns={columns}
                data={brokers || []}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                totalItems={pagination?.total || 0}
                totalPages={pagination?.totalPages || 0}
                pageSize={filters.pageSize}
                manualSorting
                sorting={sorting}
                onSortChange={nextSorting => {
                  const nextSort = nextSorting[0];
                  const sortField = nextSort
                    ? BROKER_SORT_FIELD_BY_COLUMN_ID[nextSort.id]
                    : DEFAULT_SORT_FIELD;

                  setFilters({
                    ...filters,
                    page: 1,
                    sortField: sortField ?? DEFAULT_SORT_FIELD,
                    sortType: nextSort?.desc ? 'desc' : 'asc',
                  });
                }}
                onPageChange={(page, pageSize) =>
                  setFilters({ ...filters, page, pageSize })
                }
              />
            </div>
          </>
        )}
      </Page.Content>

      {canBulkManageProducts && (
        <BulkProductsModal
          isOpen={isBulkOpen}
          onClose={() => setIsBulkOpen(false)}
          initialFilters={{
            search: debouncedFilters.search || undefined,
            advisoryId: canFilterByAdvisory
              ? debouncedFilters.advisoryId || undefined
              : undefined,
            city: debouncedFilters.city || undefined,
            state: debouncedFilters.state || undefined,
          }}
        />
      )}
    </Page.Root>
  );
}

function BrokerCardField({
  label,
  value,
  className = '',
}: {
  label: string;
  value?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className='mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
        {label}
      </p>
      <p className='break-words text-sm text-alba-gray-700'>{value || EMPTY}</p>
    </div>
  );
}
